// "2 accounts" / "1 of 3 accounts connected", with the accounts on hover (owner 2026-10-25:
// the personal area lists all the accounts connected, each project card and each project
// page the number of accounts per Procore and per ACC).
//
// `list`: [{ label, email?, connected }] - the personal area's /procore/status and
// /acc/status `accounts`, or a project's GET /integrations `accounts.<provider>.list`.
import React from "react";
import { Popover } from "antd";
import "./integrations.scss";

export function accountsText(list) {
  const all = Array.isArray(list) ? list : [];
  const on = all.filter((a) => a.connected).length;
  if (!all.length) return "no accounts";
  const n = `${all.length} account${all.length === 1 ? "" : "s"}`;
  return on === all.length ? `${n} connected` : `${on} of ${n} connected`;
}

const ago = (d) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
};
const clock = (d) => new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * THE TOKEN PING'S LAST FINDING for one account (server utils/connectionHealth, every 10
 * minutes): valid until when, being renewed, or needing someone to sign in again.
 */
export function healthText(h) {
  if (!h || !h.state) return "";
  const checked = h.checkedAt ? `checked ${ago(h.checkedAt)}` : "";
  if (h.state === "ok") return ["Token valid", h.validUntil ? `until ${clock(h.validUntil)}` : "", checked ? `· ${checked}` : ""].filter(Boolean).join(" ");
  if (h.state === "renewing") return `Renewing automatically${h.note ? ` - ${h.note}` : ""}`;
  if (h.state === "needs-sign-in") return `Needs sign-in${h.note ? ` - ${h.note}` : ""}`;
  if (h.state === "unreachable") return `Not answering, will retry${checked ? ` · ${checked}` : ""}`;
  return "";
}
export const needsSignIn = (a) => !!(a && a.health && a.health.state === "needs-sign-in");

export default function AccountsCount({ list, name, testId }) {
  const all = Array.isArray(list) ? list : [];
  const broken = all.some((a) => !a.connected || needsSignIn(a) || (a.folders || []).some((f) => !f.ok));
  const content = all.length ? (
    <div className="accounts-count-list">
      {all.map((a, i) => (
        <div key={a.id || a.key || i} className="accounts-count-row" data-testid="accounts-count-row">
          <span className={`accounts-count-dot ${a.connected && !needsSignIn(a) ? "on" : "off"}`} />
          <span>{a.label || a.email || `${name || ""} account`}</span>
          {a.email && a.email !== a.label ? <small className="text-muted">{a.email}</small> : null}
          <small className={`accounts-count-status ${a.connected ? "text-success" : "text-danger"}`}>{a.connected ? "Connected" : "Not connected"}</small>
          {a.connected && healthText(a.health) ? (
            <small className={`accounts-count-health ${a.health.state}`} data-testid="accounts-count-health">{healthText(a.health)}</small>
          ) : null}
          {(a.folders || []).filter((f) => !f.ok).map((f) => (
            <small key={`${f.kind}-${f.path}`} className="accounts-count-health needs-sign-in" data-testid="accounts-count-folder">
              Folder not reachable: {f.path || f.kind}{f.error ? ` - ${f.error}` : ""}
            </small>
          ))}
        </div>
      ))}
    </div>
  ) : (
    <span className="text-muted">No {name || ""} account yet</span>
  );
  return (
    <Popover title={name ? `${name} accounts` : "Accounts"} content={content}>
      <span className={`accounts-count ${broken ? "broken" : ""}`} data-testid={testId}>
        {accountsText(all)}
      </span>
    </Popover>
  );
}

/**
 * The accounts a project page shows for one provider, from GET /integrations/:projectId:
 * the ones the project's destinations use; a project with none yet shows the accounts the
 * viewer could use (`connections`).
 */
export function projectAccounts(data, provider) {
  const used = data && data.accounts && data.accounts[provider];
  if (used && used.list && used.list.length) return used.list;
  return (data && data.connections && data.connections[provider]) || [];
}

/**
 * Whether one destination's account works: its own account (the actor's connection) when
 * the server names it, else any account the viewer has for the provider.
 */
export function destinationConnected(data, provider, d) {
  if (d && d.actor) return !!d.actor.connected;
  const p = (data && data[provider]) || {};
  return !!p.connected || ((data && data.connections && data.connections[provider]) || []).some((a) => a.connected);
}

/** The per-provider counts at the head of a project page's Procore / ACC bar. */
export function ProviderAccounts({ data }) {
  return (
    <span className="provider-accounts">
      {[["procore", "Procore"], ["acc", "ACC"]].map(([key, name]) => (
        <span key={key} className="provider-accounts-one">
          {name}: <AccountsCount list={projectAccounts(data, key)} name={name} testId={`project-accounts-${key}`} />
        </span>
      ))}
    </span>
  );
}
